import DOMPurify from 'dompurify';
import { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

/** Parsed shape of `raw_content` when type is genai with grounding metadata. */
export interface GenAiRawGroundingContent {
  type: string;
  grounding_metadata: {
    search_entry_point: {
      rendered_content: string;
    };
  };
}

function getGenAiGroundingHtmlStringsFromOutputs(rawContents: string): string {
  try {
    const genAIRes = JSON.parse(rawContents) as GenAiRawGroundingContent;
    if (
      genAIRes.type === 'genai' &&
      genAIRes.grounding_metadata.search_entry_point.rendered_content
    ) {
      return genAIRes.grounding_metadata.search_entry_point.rendered_content;
    }
  } catch {}
  return '';
}

interface GenAiGroundingFromAgentOutputsProps {
  rawContent: string;
}

/** Renders Gen AI search grounding HTML blocks from `/get-ai-agent-outputs` rows (with raw_content). */
export function GenAiGroundingFromAgentOutputs({
  rawContent,
}: GenAiGroundingFromAgentOutputsProps) {
  const htmlContent = useMemo(
    () => getGenAiGroundingHtmlStringsFromOutputs(rawContent),
    [rawContent],
  );

  if (htmlContent.length === 0) {
    return null;
  }

  return (
    <GenAiRenderedHtmlSection>
      <GenAiRenderedHtmlBlock html={htmlContent} />
    </GenAiRenderedHtmlSection>
  );
}

const GenAiRenderedHtmlSection = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

interface GenAiRenderedHtmlBlockProps {
  html: string;
}

const GenAiRenderedHtmlBlockRoot = styled.div`
  min-width: 0;
`;

/**
 * Default DOMPurify parsing moves `<style>` into `<head>`; output uses `body.innerHTML` only, so
 * styles were dropped. `FORCE_BODY` keeps `<style>` in the sanitized fragment (see DOMPurify docs).
 */
const GEN_AI_GROUNDING_SANITIZE_CONFIG = { FORCE_BODY: true } as const;

const GenAiRenderedHtmlBlock = ({ html }: GenAiRenderedHtmlBlockProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) {
      return;
    }
    root.querySelectorAll('a[href]').forEach((anchor) => {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    });
  }, [html]);

  return (
    <GenAiRenderedHtmlBlockRoot
      ref={containerRef}
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(html, GEN_AI_GROUNDING_SANITIZE_CONFIG),
      }}
    />
  );
};
